// Денежное число на клиенте: что оно такое и чем обязано быть подписано.
//
// ⚠ **Клиент денег не считает.** Ни сложения, ни процентов, ни округления, ни
// перевода в число с плавающей точкой. Значение приходит с сервера строкой —
// так, как его отдал `core.compute`, — и живёт строкой до самого показа.
// Причина не в аккуратности: `Decimal` сервера в `number` теряет копейку, а
// копейка в системе, которая выводит компанию из долга, — это расхождение с
// бухгалтерией, которое потом ищут днями.
//
// ⚠ Три вещи здесь обязательны **типом**, а не договорённостью между авторами:
// подсказка «?», источник числа и база. Пропуск любой из них — ошибка сборки
// TypeScript, а не замечание на ревью. Так решено потому, что замечание на
// ревью ловит одного автора, а тип — всех и навсегда.

/** Гривня. Другой валюты в системе нет. */
export type Currency = "UAH";

/**
 * Денежная величина, посчитанная ядром.
 *
 * `amount` — десятичная строка ровно в том виде, в каком её отдал сервер.
 * Знак допустим: минус в системе законен (`НЧ`), и прятать его нельзя.
 */
export interface Money {
  readonly amount: string;
  readonly currency: Currency;
}

/**
 * Источник числа (`DEC-O7`). Ровно два, третьего не бывает: без пометки
 * запись из чата в расчёте участвовать не может.
 */
export type Source = "bookkeeper" | "chat";

/**
 * База числа: период и фильтр — то, из какого набора документов оно сложилось.
 *
 * ⚠ Две базы одной величины никогда не показываются без подписи, какая из них
 * какая (`SCREEN-MAP.md` §10; ловушка 2 — `DATA.md` §8). Поэтому база — не
 * украшение, а часть числа: без неё «Вільно» за две недели и «Вільно» всего
 * выглядят одинаково.
 */
export interface Base {
  /** Период словами: «на 14 днів», «вся сума, без горизонту», «01.09–14.09». */
  readonly period: string;
  /** Набор документов: «вихідні платежі», «підтверджені надходження». */
  readonly filter: string;
}

/**
 * Откуда взялось правило. Внутри подсказки стоит ссылка на решение или на
 * раздел брифа (`SCREEN-MAP.md` §10): весь проект держится на
 * прослеживаемости, и до экрана она доходить обязана.
 *
 * Тип, а не строка: «см. документацию» здесь не собирается.
 */
export type RuleRef = `DEC-${string}` | `G${number}` | `FRM-${string}` | `BRIEF.md §${string}`;

/**
 * Подсказка «?». `G117` (таск 74): у каждого элемента своя подсказка — **что это
 * и как читать**, механика расчёта — сноской. Второе назначение — разгрузка
 * поверхности (`G44`): подробности живут здесь, а не в подписи. Прежний состав
 * `G21` (что это · для чего · как работает · на что влияет) сведён сюда же: «для
 * чего» и «на что влияет» — часть «как читать», «как работает» — сноска.
 *
 * ⚠ Все три поля обязательны. Подсказка без поля — это подсказка, на которую
 * человек нажал и не получил ответа; она хуже её отсутствия, потому что второй
 * раз он уже не нажмёт.
 */
export interface Hint {
  /** Что это. Одно-два предложения простыми словами. */
  readonly what: string;
  /** Как читать: что значит число или блок и какое решение он меняет. */
  readonly read: string;
  /** Сноска: как это посчитано или откуда взято — механика словами. */
  readonly note: string;
  /** Откуда правило: решение, требование владельца, формула или бриф. На экран не идёт. */
  readonly rule: RuleRef;
}

/**
 * Подсказка поля ввода (F-087): к «що це · як читати · сноска» добавлены
 * обязательные «приклад» и «якщо порожньо» — тип не даёт завести поле без них.
 */
export interface FieldHint extends Hint {
  /** Пример значения: в пустом поле и в плашке окна «?». */
  readonly example: string;
  /** Что будет, если поле пусто, — одна строка (`if_empty`, с направлением ошибки). */
  readonly empty: string;
}

/** Подсказка собрана, но пуста. Пустое поле — то же, что его отсутствие. */
export class HollowHint extends Error {}

/** Число показано так, что его можно прочитать двумя способами. */
export class AmbiguousMoney extends Error {}

/**
 * Проверка подсказки на живом объекте.
 *
 * Тип ловит пропущенное поле, но не ловит пустую строку: `what: ""` собирается.
 * Поэтому пустота проверяется ещё раз при показе — и падает громко, а не
 * рисует пустой блок.
 */
export function assertHintFilled(hint: Hint | FieldHint, where: string): void {
  const hollow = (Object.keys(hint) as (keyof FieldHint)[]).filter(
    (key) => String((hint as FieldHint)[key]).trim() === "",
  );
  if (hollow.length > 0) {
    throw new HollowHint(
      `${where}: подсказка «?» пуста в полях ${hollow.join(", ")}. ` +
        "Подсказка без ответа хуже её отсутствия: второй раз человек не нажмёт.",
    );
  }
}

/** Десятичная строка — то, что отдаёт `core.compute`, и ничего сверх. */
const DECIMAL = /^-?\d+(\.\d+)?$/;

/**
 * Проверка значения на входе в показ.
 *
 * Здесь ловится ровно один дефект: в `amount` пришло не число, а результат
 * чьей-то арифметики на клиенте — `NaN`, `Infinity`, `undefined`, приведённые
 * к строке. Само число не трогается: ни округления, ни нормализации.
 */
export function assertMoneyReadable(value: Money, where: string): void {
  if (!DECIMAL.test(value.amount)) {
    throw new AmbiguousMoney(
      `${where}: «${value.amount}» — не десятичное число с сервера. ` +
        "Клиент денег не считает; значение приходит строкой из core.compute.",
    );
  }
}
